// Angular
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

// Libraries
import { takeUntil } from 'rxjs';
// Models
import { AppCleanup, DynamicTableColumn } from '../../shared/models';
import { DynamicMenuButton } from '../../shared/models/dynamic-menu-button.model';
import { Job } from '../../modules/job/models';
import { JobTypeChannelMappingDto } from '../../modules/job/models/job-type-channel-mapping-dto.model';
import { ConfirmDialogModel } from '../../shared/dialogs/confirm-dialog/confirm-dialog.component';

// Services
import { JobCommentService } from '../../modules/comment/services/job-comment.service';
import { JobService } from '../../modules/job/services/job.service';
import { DialogService } from '../../shared/services/dialog.service';
import { SpinnerService } from '../../shared/components/spinner/services/spinner.service';
import { TranslateService } from '@ngx-translate/core';

// Components
import { JobChannelMappingEditDialogComponent } from '../job-channel-mapping-edit-dialog/job-channel-mapping-edit-dialog.component';

@Component({
	selector: 'job-channel-mapping-dialog',
	templateUrl: './job-channel-mapping-dialog.component.html',
	styleUrls: ['./job-channel-mapping-dialog.component.scss'],
})
export class JobChannelMappingDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	componentTitle: string = 'Channel overrides for JDR';
	channelMappings: JobTypeChannelMappingDto[];
	columnList: DynamicTableColumn[] = [
		{
			name: 'jobTypeChannel.fieldName',
			display: 'File Name',
			width: 1,
		},
		{
			name: 'channel.label',
			display: 'Job Type',
			width: 1,
		},
	];
	menuButtons: DynamicMenuButton[] = [
		{
			matIcon: 'edit',
			name: 'Edit',
			event: this.editMapping.bind(this),
		},
		{
			matIcon: 'close',
			name: 'Delete',
			event: this.deleteMapping.bind(this),
		},
	];

	constructor(
		public dialogRef: MatDialogRef<JobChannelMappingDialogComponent>,
		private jobCommentService: JobCommentService,
		private jobService: JobService,
		private dialog: MatDialog,
		private dialogService: DialogService,
		private spinnerService: SpinnerService,
		private translateService: TranslateService,
		@Inject(MAT_DIALOG_DATA) public data: { job: Job }
	) {
		super();
	}

	ngOnInit() {
		this.channelMappings = this.data.job.jobTypeChannelMappings.filter((jtcm) => !jtcm.isDeleted);
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	addMapping() {
		let mapping: JobTypeChannelMappingDto = {
			id: 0,
			jobId: this.data.job.id,
			jobTypeChannelId: null,
			channelId: null,
			isDeleted: false,
		};

		const dialogRef = this.dialog.open(JobChannelMappingEditDialogComponent, {
			data: {
				job: this.data.job,
				jobTypeChannelMapping: mapping,
			},
			width: '100%',
			maxWidth: '400px',
			panelClass: 'jobChannelMappingEdit',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((data) => {
				if (!data) return;
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner

				mapping.jobTypeChannelId = data.jobTypeChannelId;
				mapping.channelId = data.channelId;

				this.jobCommentService
					.addJobTypeChannelMapping(mapping)
					.pipe(takeUntil(this.destroy))
					.subscribe((jobTypeChannelMapping) => {
						this.data.job = this.jobService.modifyChannelMappingForJob(jobTypeChannelMapping);
						this.updateData();
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					});
			});
	}

	editMapping(mapping: JobTypeChannelMappingDto) {
		const dialogRef = this.dialog.open(JobChannelMappingEditDialogComponent, {
			data: {
				job: this.data.job,
				jobTypeChannelMapping: mapping,
			},
			width: '100%',
			maxWidth: '400px',
			panelClass: 'jobChannelMappingEdit',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((data) => {
				if (!data) return;
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner

				mapping.jobTypeChannelId = data.jobTypeChannelId;
				mapping.channelId = data.channelId;

				this.jobCommentService
					.editJobTypeChannelMapping(mapping)
					.pipe(takeUntil(this.destroy))
					.subscribe((jobTypeChannelMapping) => {
						this.data.job = this.jobService.modifyChannelMappingForJob(jobTypeChannelMapping);
						this.updateData();
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					});
			});
	}

	deleteMapping(mapping: JobTypeChannelMappingDto) {
		const confirmModel: ConfirmDialogModel = {
			title: 'Delete channel mapping',
			message: 'Are you sure you want to delete this channel mapping?',
			action: this.translateService.instant('ACTION.DELETE_CAPITAL'),
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);
		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					this.spinnerService.showAppSpinner(); // Turns on app wide spinner
					this.jobCommentService
						.deleteJobTypeChannelMapping(mapping)
						.pipe(takeUntil(this.destroy))
						.subscribe(() => {
							mapping.isDeleted = !mapping.isDeleted;
							this.data.job = this.jobService.modifyChannelMappingForJob(mapping);
							this.updateData();
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						});
				}
			});
	}

	updateData() {
		let channelMappings = this.data.job.jobTypeChannelMappings.filter((jtcm) => !jtcm.isDeleted);
		this.channelMappings = [...channelMappings];
	}
}
